// Página PÚBLICA de validação da Certidão Nada Consta — é o que o QR Code
// impresso na certidão abre (/validar-certidao/:codigo). Fica fora do
// ProtectedLayout (App.js) e não usa o Header do portal, porque quem confere a
// certidão normalmente não tem login.

import React, { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import styled from 'styled-components';
import { colors, breakpoints, fadeIn } from '../../../assets/styles/GlobalStyles';
import LOGO from '../../../assets/images/logo-oabms.png';
import { certidaoNadaConstaService, formatarDataCertidao } from '../services/certidaoNadaConstaService';
import { FaCheckCircle, FaClock, FaTimesCircle, FaSpinner, FaRedo } from 'react-icons/fa';

const SITUACOES = {
  VALIDA: {
    titulo: 'Certidão válida',
    descricao: 'Esta certidão foi emitida pela OAB/MS e está dentro do prazo de validade.',
    icone: FaCheckCircle,
    cor: '#166534',
    fundo: '#f0fdf4',
    borda: '#bbf7d0'
  },
  EXPIRADA: {
    titulo: 'Certidão expirada',
    descricao: 'Esta certidão foi emitida pela OAB/MS, mas o prazo de validade já terminou.',
    icone: FaClock,
    cor: '#92400e',
    fundo: '#fffbeb',
    borda: '#fde68a'
  }
};

const Page = styled.div`
  min-height: 100vh;
  min-height: 100dvh;
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 3rem 1rem 2rem;
  font-family: 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif;
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);

  @media (max-width: ${breakpoints.mobile}) {
    padding-top: 2rem;
  }
`;

const Logo = styled.img`
  height: 64px;
  margin-bottom: 1.5rem;
`;

const Card = styled.div`
  width: 100%;
  max-width: 560px;
  background: ${colors.white};
  border-radius: 16px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.06);
  border: 1px solid rgba(0, 0, 0, 0.05);
  padding: 2rem;
  animation: ${fadeIn} 0.4s ease-out;

  @media (max-width: 480px) {
    padding: 1.25rem;
  }
`;

const Title = styled.h1`
  font-size: 1.25rem;
  font-weight: 700;
  color: ${colors.darkBlue};
  margin: 0 0 0.25rem 0;
  text-align: center;
`;

const Subtitle = styled.p`
  font-size: 0.85rem;
  color: ${colors.textMuted};
  margin: 0 0 1.5rem 0;
  text-align: center;
`;

const Status = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 1rem 1.25rem;
  border-radius: 12px;
  background: ${props => props.$fundo};
  border: 1px solid ${props => props.$borda};
  margin-bottom: 1.5rem;

  svg {
    flex-shrink: 0;
    font-size: 1.6rem;
    color: ${props => props.$cor};
  }

  strong {
    display: block;
    font-size: 1.05rem;
    color: ${props => props.$cor};
    margin-bottom: 2px;
  }

  p {
    margin: 0;
    font-size: 0.85rem;
    color: #374151;
    line-height: 1.5;
  }
`;

const Dados = styled.dl`
  margin: 0;
  display: flex;
  flex-direction: column;

  div {
    display: flex;
    justify-content: space-between;
    gap: 1rem;
    padding: 0.7rem 0;
    border-bottom: 1px solid ${colors.borderLight};

    &:last-child {
      border-bottom: none;
    }

    @media (max-width: 480px) {
      flex-direction: column;
      gap: 2px;
    }
  }

  dt {
    font-size: 0.85rem;
    color: ${colors.textMuted};
  }

  dd {
    margin: 0;
    font-size: 0.9rem;
    font-weight: 600;
    color: #1a2c3e;
    text-align: right;
    overflow-wrap: anywhere;

    @media (max-width: 480px) {
      text-align: left;
    }
  }
`;

const Centro = styled.div`
  text-align: center;
  padding: 1.5rem 0 0.5rem;
  color: ${colors.textMuted};
  font-size: 0.9rem;

  > svg {
    font-size: 2.5rem;
    margin-bottom: 0.75rem;
    color: ${props => props.$cor || colors.lightBlue};
  }

  .spin {
    animation: girar 1s linear infinite;
  }

  @keyframes girar {
    to { transform: rotate(360deg); }
  }

  strong {
    display: block;
    color: #1a2c3e;
    font-size: 1.05rem;
    margin-bottom: 0.4rem;
  }

  p {
    margin: 0;
    line-height: 1.5;
  }
`;

const RetryButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 8px;
  margin-top: 1.25rem;
  background: ${colors.darkBlue};
  color: ${colors.white};
  border: none;
  padding: 10px 24px;
  border-radius: 8px;
  font-size: 0.9rem;
  font-weight: 500;
  font-family: inherit;
  cursor: pointer;

  &:hover {
    background: ${colors.blueLight};
  }
`;

const Rodape = styled.p`
  max-width: 560px;
  margin: 1.5rem 0 0 0;
  font-size: 0.75rem;
  color: ${colors.textMuted};
  text-align: center;
  line-height: 1.5;
`;

const ValidarCertidaoPage = () => {
  const { codigo } = useParams();
  const [loading, setLoading] = useState(true);
  const [certidao, setCertidao] = useState(null);
  const [erro, setErro] = useState(null);

  const consultar = async () => {
    setLoading(true);
    setErro(null);

    const response = await certidaoNadaConstaService.validar(codigo);

    if (response.success) {
      setCertidao(response.data);
    } else {
      setCertidao(null);
      setErro(response);
    }

    setLoading(false);
  };

  useEffect(() => {
    consultar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [codigo]);

  const situacao = certidao && SITUACOES[certidao.situacao];

  return (
    <Page>
      <Logo src={LOGO} alt="OAB/MS" />

      <Card>
        <Title>Validação de Certidão Nada Consta</Title>
        <Subtitle>Código: {codigo}</Subtitle>

        {loading && (
          <Centro>
            <FaSpinner className="spin" />
            <p>Consultando certidão...</p>
          </Centro>
        )}

        {!loading && erro && (
          <Centro $cor={colors.red}>
            <FaTimesCircle />
            <strong>{erro.status === 404 ? 'Certidão não encontrada' : 'Não foi possível validar a certidão'}</strong>
            <p>{erro.error}</p>
            {erro.status !== 404 && erro.status !== 400 && (
              <RetryButton onClick={consultar}>
                <FaRedo /> Tentar novamente
              </RetryButton>
            )}
          </Centro>
        )}

        {!loading && certidao && (
          <>
            {situacao && (
              <Status $cor={situacao.cor} $fundo={situacao.fundo} $borda={situacao.borda}>
                <situacao.icone />
                <div>
                  <strong>{situacao.titulo}</strong>
                  <p>{situacao.descricao}</p>
                </div>
              </Status>
            )}

            <Dados>
              <div>
                <dt>Nome</dt>
                <dd>{certidao.nome || '—'}</dd>
              </div>
              <div>
                <dt>CPF</dt>
                <dd>{certidao.cpf || '—'}</dd>
              </div>
              <div>
                <dt>Inscrição OAB/MS</dt>
                <dd>{certidao.registroConselho || '—'}</dd>
              </div>
              <div>
                <dt>Emitida em</dt>
                <dd>{formatarDataCertidao(certidao.emitidaEm)}</dd>
              </div>
              <div>
                <dt>Válida até</dt>
                <dd>{formatarDataCertidao(certidao.validaAte)}</dd>
              </div>
            </Dados>
          </>
        )}
      </Card>

      <Rodape>
        Confira se os dados acima são os mesmos impressos na certidão apresentada.
        Ordem dos Advogados do Brasil — Seccional Mato Grosso do Sul.
      </Rodape>
    </Page>
  );
};

export default ValidarCertidaoPage;
